import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Sparkles,
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Wand2,
  Plus,
  X,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AITemplateConverter({ onTemplateCreated }) {
  const { token } = useAuth();

  // Dialog state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Review suggestions, 3: Configure & Create

  // File state
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  // Selection state
  const [selectedReplacements, setSelectedReplacements] = useState({});
  const [selectedNewFields, setSelectedNewFields] = useState({});

  // Template config
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('templates');
  const [creating, setCreating] = useState(false);

  const resetState = () => {
    setStep(1);
    setFile(null);
    setAnalysis(null);
    setSelectedReplacements({});
    setSelectedNewFields({});
    setTemplateName('');
    setTemplateDescription('');
    setTemplateCategory('templates');
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.docx')) {
        toast.error('Only .docx files are supported');
        return;
      }
      setFile(selectedFile);
      setTemplateName(selectedFile.name.replace('.docx', ''));
    }
  };

  const analyzeDocument = async () => {
    if (!file) return;

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/case-management/document-templates/analyze-for-conversion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setAnalysis(data);
        
        // Pre-select high confidence suggestions
        const preSelected = {};
        const preSelectedNewFields = {};
        
        data.analysis?.suggestions?.forEach((s, idx) => {
          if (s.confidence === 'high') {
            preSelected[idx] = true;
          }
        });
        
        data.analysis?.new_custom_fields_needed?.forEach((f, idx) => {
          preSelectedNewFields[idx] = true;
        });
        
        setSelectedReplacements(preSelected);
        setSelectedNewFields(preSelectedNewFields);
        setStep(2);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to analyze document');
      }
    } catch (error) {
      toast.error('Failed to analyze document');
    } finally {
      setAnalyzing(false);
    }
  };

  const createTemplate = async () => {
    if (!templateName) {
      toast.error('Please enter a template name');
      return;
    }

    // Build replacement list from selected items
    const replacements = [];
    analysis?.analysis?.suggestions?.forEach((s, idx) => {
      if (selectedReplacements[idx]) {
        replacements.push({
          original_text: s.original_text,
          tag: s.suggested_tag
        });
      }
    });

    // Build new fields list
    const newFields = [];
    analysis?.analysis?.new_custom_fields_needed?.forEach((f, idx) => {
      if (selectedNewFields[idx]) {
        newFields.push(f);
      }
    });

    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', templateName);
      formData.append('description', templateDescription);
      formData.append('category', templateCategory);
      formData.append('replacements', JSON.stringify(replacements));
      formData.append('create_new_fields', JSON.stringify(newFields));

      const res = await fetch(`${API_URL}/api/case-management/document-templates/convert-with-ai`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Template created with ${data.merge_fields_found?.length || 0} merge fields!`);
        
        if (data.created_custom_fields?.length > 0) {
          toast.info(`Created ${data.created_custom_fields.length} new custom fields`);
        }
        
        setOpen(false);
        resetState();
        if (onTemplateCreated) onTemplateCreated();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      toast.error('Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const toggleReplacement = (idx) => {
    setSelectedReplacements(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const toggleNewField = (idx) => {
    setSelectedNewFields(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'low': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const selectedCount = Object.values(selectedReplacements).filter(Boolean).length;
  const newFieldsCount = Object.values(selectedNewFields).filter(Boolean).length;

  return (
    <>
      <Button 
        variant="outline" 
        onClick={() => { resetState(); setOpen(true); }}
        data-testid="ai-convert-btn"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        AI Convert
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) resetState(); setOpen(v); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Template Converter
            </DialogTitle>
            <DialogDescription>
              {step === 1 && "Upload a document and AI will identify text to convert to merge fields"}
              {step === 2 && "Review AI suggestions and select which replacements to apply"}
              {step === 3 && "Configure your template and create it"}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-purple-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-purple-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="flex-1 py-4 space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="ai-file-input"
                />
                <label htmlFor="ai-file-input" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">
                    {file ? file.name : 'Drop your document here'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    or click to browse (.docx files only)
                  </p>
                </label>
              </div>

              {file && (
                <div className="p-4 bg-purple-50 rounded-lg flex items-center gap-4">
                  <FileText className="w-10 h-10 text-purple-600" />
                  <div className="flex-1">
                    <p className="font-medium text-purple-800">{file.name}</p>
                    <p className="text-sm text-purple-600">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
              )}

              <div className="p-4 bg-amber-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">How it works</p>
                    <p className="text-sm text-amber-700 mt-1">
                      AI will analyze your document and identify names, dates, amounts, and other 
                      variable text that should become merge fields. You'll review each suggestion 
                      before the template is created.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Review suggestions */}
          {step === 2 && analysis && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4 py-4">
                {/* Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Analysis Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {analysis.analysis?.summary || 'Document analyzed successfully'}
                    </p>
                    <div className="flex gap-4 mt-3">
                      <Badge variant="outline">
                        {analysis.analysis?.suggestions?.length || 0} replacements found
                      </Badge>
                      <Badge variant="outline">
                        {analysis.analysis?.new_custom_fields_needed?.length || 0} new fields suggested
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Suggested replacements */}
                {analysis.analysis?.suggestions?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      Suggested Replacements
                      <Badge variant="secondary">{selectedCount} selected</Badge>
                    </h4>
                    <div className="space-y-2">
                      {analysis.analysis.suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          onClick={() => toggleReplacement(idx)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedReplacements[idx]
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-slate-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedReplacements[idx] || false}
                              onCheckedChange={() => toggleReplacement(idx)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm line-through">
                                  {suggestion.original_text}
                                </span>
                                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                <code className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-mono">
                                  {suggestion.suggested_tag}
                                </code>
                                <Badge className={getConfidenceColor(suggestion.confidence)}>
                                  {suggestion.confidence}
                                </Badge>
                                {suggestion.is_new_tag && (
                                  <Badge variant="outline" className="text-xs">New field</Badge>
                                )}
                              </div>
                              {suggestion.reason && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {suggestion.reason}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New custom fields needed */}
                {analysis.analysis?.new_custom_fields_needed?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      New Custom Fields to Create
                      <Badge variant="secondary">{newFieldsCount} selected</Badge>
                    </h4>
                    <div className="space-y-2">
                      {analysis.analysis.new_custom_fields_needed.map((field, idx) => (
                        <div
                          key={idx}
                          onClick={() => toggleNewField(idx)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedNewFields[idx]
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-slate-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedNewFields[idx] || false}
                              onCheckedChange={() => toggleNewField(idx)}
                              className="mt-1"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{field.name}</span>
                                <code className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-mono">
                                  {`{{${field.tag}}}`}
                                </code>
                                <Badge variant="outline" className="text-xs">
                                  {field.field_type}
                                </Badge>
                              </div>
                              {field.description && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {field.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Document preview */}
                {analysis.document_preview && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Document Preview
                    </h4>
                    <div className="p-3 bg-slate-50 rounded-lg text-sm font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {analysis.document_preview}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          {/* Step 3: Configure */}
          {step === 3 && (
            <div className="flex-1 py-4 space-y-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Retainer Agreement"
                  data-testid="ai-template-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description of this template..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={templateCategory} onValueChange={setTemplateCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pleadings">Pleadings</SelectItem>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="correspondence">Correspondence</SelectItem>
                    <SelectItem value="contracts">Contracts</SelectItem>
                    <SelectItem value="templates">General Templates</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-purple-800 mb-2">Conversion Summary</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• {selectedCount} text replacement{selectedCount !== 1 ? 's' : ''} will be applied</li>
                    <li>• {newFieldsCount} new custom field{newFieldsCount !== 1 ? 's' : ''} will be created</li>
                    <li>• Original document: {file?.name}</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            {step === 1 && (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={analyzeDocument} 
                  disabled={!file || analyzing}
                  data-testid="analyze-document-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button onClick={() => setStep(3)} disabled={selectedCount === 0}>
                  Continue ({selectedCount} selected)
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {step === 3 && (
              <>
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button 
                  onClick={createTemplate} 
                  disabled={creating || !templateName}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="create-ai-template-btn"
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Wand2 className="w-4 h-4 mr-2" />
                  )}
                  {creating ? 'Creating...' : 'Create Template'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
